import React, { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/auth'

interface ProtectedRouteProps {
  children: React.ReactNode
  requireAuth?: boolean
  requireAdmin?: boolean
  requireModerator?: boolean
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  requireAdmin = false,
  requireModerator = false
}) => {
  const { isAuthenticated, user, checkAuth, isLoading, token } = useAuthStore()
  const location = useLocation()
  const [authChecked, setAuthChecked] = useState(false)
  const [authCheckAttempted, setAuthCheckAttempted] = useState(false)

  useEffect(() => {
    const initializeAuth = async () => {
      // 如果已经有token但状态还没恢复，尝试检查认证
      // 但是要避免在刚登录后立即进行严格检查
      if (token && !isAuthenticated) {
        // 延迟执行，避免与登录流程的竞争条件
        setTimeout(async () => {
          try {
            await checkAuth()
          } catch (error) {
            console.warn('Auth check failed during route protection:', error)
            // 如果认证检查失败，清除本地token，重新登录
            // 这样可以避免无限循环
            setTimeout(() => {
              window.location.href = '/auth'
            }, 100)
          }
        }, 800) // 增加延迟时间
      }
      setAuthChecked(true)
      setAuthCheckAttempted(true)
    }

    // 只有在还没有检查过认证的情况下才执行
    if (!authCheckAttempted) {
      initializeAuth()
    }
  }, [checkAuth, token, isAuthenticated, authCheckAttempted])

  // 显示加载状态 - 更宽松的条件，避免过度显示加载状态
  if (!authChecked || (isLoading && !token)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">正在验证身份...</p>
        </div>
      </div>
    )
  }

  // 检查认证要求
  if (requireAuth && !isAuthenticated && !token) {
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  // 检查管理员权限
  if (requireAdmin && user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md">
            <div className="text-red-600 text-6xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              权限不足
            </h2>
            <p className="text-gray-600 mb-4">
              此页面需要管理员权限才能访问。
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              返回上一页
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 检查版主权限
  if (requireModerator && !['admin', 'moderator'].includes(user?.role || '')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md">
            <div className="text-red-600 text-6xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              权限不足
            </h2>
            <p className="text-gray-600 mb-4">
              此页面需要版主或管理员权限才能访问。
            </p>
            <button
              onClick={() => window.history.back()}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              返回上一页
            </button>
          </div>
        </div>
      </div>
    )
  }

  // 检查账户状态
  if (isAuthenticated && user && !user.is_active) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md">
            <div className="text-yellow-600 text-6xl mb-4">⚠️</div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              账户已停用
            </h2>
            <p className="text-gray-600 mb-4">
              您的账户已被管理员停用。如有疑问，请联系管理员。
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              返回首页
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
